<template>
  <teleport to="body">
    <pdl-drawer
      :style="headerStyle"
      :is-open="isDrawerOpen"
      :size="getDrawerWidth"
      :modal="false"
      qaid="custom-components-drawer"
      @keydown.esc="updateConfigToCurrentSelections"
    >
      <template #content>
        <div class="pl-2 pt-2 pb-2 justify-start bg-gray-02">
          <pdl-button
            small
            tertiary
            text
            icon="chevron_left"
            qaid="custom-components-drawer-back-button"
            @click="updateConfigToCurrentSelections"
          >
            {{ $t('projectOne.buyingZone.customization.components') }}
          </pdl-button>
        </div>
        <fieldset role="radiogroup" aria-required="true">
          <legend class="text-md font-bold pb-2 pl-2 bg-gray-02 w-full" qaid="custom-components-drawer-component">
            {{ componentDescription }}
          </legend>
          <div class="absolute top-30 bottom-12 left-0 right-0 px-2 overflow-y-auto mb-3">
            <customization-suspension-calculator v-if="isShockComponentGroup" />
            <customization-group-selector-card
              v-for="option in rearrangedComponentList"
              :key="option.id"
              :component-name="option.description"
              :description="option.marketingCopy"
              :price-difference="option.priceDifferenceAdvertised"
              :asset-id="option.imageThumbnailReference"
              :component-group-id="componentGroupId"
              :component-option-id="option.id"
              :current-selection-id="currentSelection?.choiceId"
              @update-selection="updateComponentSelections"
              @keyup.space="handleSelect"
            />
          </div>
        </fieldset>
        <div class="absolute bottom-3 left-4 right-4 mt-3">
          <pdl-button block qaid="custom-components-drawer-select-button" @click="handleSelect">
            {{ $t('form.checkout.select') }}
          </pdl-button>
        </div>
      </template>
    </pdl-drawer>
  </teleport>
</template>

<script>
import {PdlButton} from '@pedal/pdl-button';
import {PdlDrawer} from '@pedal/pdl-drawer';
import CustomizationGroupSelectorCard from '@/components/containers/buy-zone/customization/CustomizationGroupSelectorCard.vue';
import {mapState, mapActions, mapGetters} from 'vuex';
import {PdlToastType} from '@/constants/pdl-toast-type';
import CustomizationSuspensionCalculator from '@/components/containers/buy-zone/customization/CustomizationSuspensionCalculator.vue';

const SHOCK_COMPNENT_GROUP_ID = 84;

export default {
  components: {
    PdlButton,
    PdlDrawer,
    CustomizationGroupSelectorCard,
    CustomizationSuspensionCalculator,
  },
  props: {
    isDrawerOpen: {
      type: Boolean,
      default: false,
    },
    componentDescription: {
      type: String,
      default: '',
    },
    componentChoices: {
      type: Array,
      default: () => [],
    },
    componentGroupId: {
      type: Number,
      default: 0,
    },
  },
  emits: ['back-to-custom-components-list'],
  data() {
    return {
      currentSelections: [],
      currentSelectionId: null,
      rearrangedComponentList: [],
    };
  },
  computed: {
    ...mapState('global', ['isCompactView']),
    ...mapGetters('bikeCustomization', ['selections']),

    isShockComponentGroup() {
      return this.componentGroupId === SHOCK_COMPNENT_GROUP_ID;
    },
    getDrawerWidth() {
      return !this.isCompactView ? '30rem' : '100%';
    },
    headerStyle() {
      return {
        '--customization-img-top': 'min(35vh, calc(100vw * 9 / 16))',
      };
    },
    currentSelection() {
      return this.selections.find((selection) => selection.groupId === this.componentGroupId);
    },
  },
  watch: {
    componentGroupId() {
      const selectedComponent = this.selections.find((selection) => selection.groupId === this.componentGroupId);
      this.currentSelectionId = selectedComponent?.choiceId;
      this.currentSelections = this.selections;
      this.rearrangedComponentList = this.setRearrangedComponentList();
    },
  },
  methods: {
    ...mapActions('bikeCustomization', ['updateSelection']),

    setRearrangedComponentList() {
      if (!this.componentChoices?.length || !this.currentSelection?.choiceId) {
        return [];
      }

      const currentSelection = this.componentChoices.find((choice) => choice.id === this.currentSelection.choiceId);
      if (!currentSelection) {
        return [...this.componentChoices];
      }

      const remainingChoices = this.componentChoices.filter((choice) => choice.id !== this.currentSelection.choiceId);
      return [currentSelection, ...remainingChoices];
    },

    async updateComponentSelections(id) {
      const selections = [
        {
          groupId: this.componentGroupId,
          choiceId: id,
          topTierPackageId: this.selections.find((selection) => selection.groupId === this.componentGroupId)
            ?.topTierPackageId,
        },
      ];
      try {
        await this.updateSelection({selections, module: 'components'});
      } catch (error) {
        this.$emit('back-to-custom-components-list');
        this.$notify({
          type: PdlToastType.ERROR,
          message: this.$t('text.account.savedLists.unableChange'),
        });
      }
    },

    handleSelect() {
      this.$emit('back-to-custom-components-list');
      this.$notify({
        type: PdlToastType.SUCCESS,
        message: this.$t('projectOne.customize.added'),
      });
    },

    async updateConfigToCurrentSelections() {
      const selections = [
        {
          groupId: this.componentGroupId,
          choiceId: this.currentSelectionId,
          topTierPackageId: this.selections.find((selection) => selection.groupId === this.componentGroupId)
            ?.topTierPackageId,
        },
      ];

      try {
        await this.updateSelection({selections, module: 'components'});
      } catch (error) {
        console.error('Error updating config to saved selections:', error);
        this.$notify({
          type: PdlToastType.ERROR,
          message: this.$t('text.account.savedLists.unableChange'),
        });
      } finally {
        this.$emit('back-to-custom-components-list');
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.pdl-drawer {
  z-index: 9997;
  top: calc(var(--customization-img-top) + var(--header-height));
  height: calc(100dvh - var(--customization-img-top) - var(--header-height));

  @media (screen(lg)) {
    top: var(--header-height);
    height: calc(100% - var(--header-height));
  }
}

.pdl-drawer--open {
  :deep(.pdl-drawer__content-wrapper) {
    @media (screen(lg)) {
      box-shadow: -10px 0 50px 0 rgba(0, 0, 0, 0.29);
    }
  }
}

.pdl-drawer--right {
  --max-width: 100%;
}

:deep(.pdl-drawer__content) {
  display: flex;
  flex-direction: column;
  padding: 0;
}
</style>
